import { useEffect, useState } from "react";
import { Sidebar, type TabKey } from "./components/Sidebar";
import { AgentTab } from "./tabs/AgentTab";
import { CharactersTab } from "./tabs/CharactersTab";
import { SettingsTab } from "./tabs/SettingsTab";
import { ScenesTab } from "./tabs/ScenesTab";
import { EditTab } from "./tabs/EditTab";
import { ProjectGallery } from "./tabs/ProjectGallery";
import { AuthScreen } from "./tabs/AuthScreen";
import { ConfigDialog } from "./components/ConfigDialog";
import { ToastProvider } from "./lib/toast";
import { api } from "./lib/api";
import { startLogStream } from "./lib/logs";
import type { Project, User } from "@flowgen/shared";

interface OpenRouterStatus {
  activeSource: "stored" | "env" | "none";
  hasStoredKey: boolean;
  hasEnvKey: boolean;
  baseUrl: string;
  defaultModel: string;
  storedKeyMasked: string | null;
  storedKeyUpdatedAt: string | null;
}

type AppMode = "gallery" | "workspace";

export function App() {
  const [user, setUser] = useState<User | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [mode, setMode] = useState<AppMode>("gallery");
  const [project, setProject] = useState<Project | null>(null);
  const [tab, setTab] = useState<TabKey>("agent");
  const [counts, setCounts] = useState<Record<TabKey, number>>({
    agent: 0, characters: 0, settings: 0, scenes: 0, edit: 0,
  });
  const [backendOk, setBackendOk] = useState<boolean | null>(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [openrouter, setOpenrouter] = useState<OpenRouterStatus | null>(null);

  useEffect(() => {
    if (!api.getToken()) {
      setAuthChecked(true);
      return;
    }
    api.get<{ user: User }>("/auth/me")
      .then((res) => setUser(res.user))
      .catch(() => {
        api.setToken(null);
        api.setActiveProjectId(null);
      })
      .finally(() => setAuthChecked(true));
  }, []);

  // Restore last project once signed in.
  useEffect(() => {
    if (!user) return;
    const saved = api.getActiveProjectId();
    if (!saved) return;
    api.get<Project>(`/projects/${saved}`)
      .then((p) => {
        setProject(p);
        setMode("workspace");
      })
      .catch(() => {
        api.setActiveProjectId(null);
      });
  }, [user]);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    async function refresh() {
      try {
        const cfg = await api.get<OpenRouterStatus>("/config/openrouter");
        if (!alive) return;
        setBackendOk(true);
        setOpenrouter(cfg);

        if (mode === "workspace" && api.getActiveProjectId()) {
          const [a, c, s, sc, e] = await Promise.all([
            api.get<unknown[]>("/agent/runs"),
            api.get<unknown[]>("/characters"),
            api.get<unknown[]>("/settings"),
            api.get<unknown[]>("/scenes"),
            api.get<unknown[]>("/edits"),
          ]);
          if (!alive) return;
          setCounts({
            agent: a.length,
            characters: c.length,
            settings: s.length,
            scenes: sc.length,
            edit: e.length,
          });
        }
      } catch (err: any) {
        if (!alive) return;
        if (err?.status === 401) {
          api.setToken(null);
          setUser(null);
          return;
        }
        setBackendOk(false);
      }
    }
    refresh();
    const t = setInterval(refresh, 5000);
    return () => { alive = false; clearInterval(t); };
  }, [user, mode, project?.id]);

  useEffect(() => {
    if (!user) return;
    const stop = startLogStream();
    return () => { stop(); };
  }, [user]);

  async function logout() {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    api.setToken(null);
    api.setActiveProjectId(null);
    setUser(null);
    setProject(null);
    setMode("gallery");
    setOpenrouter(null);
  }

  function openProject(p: Project) {
    api.setActiveProjectId(p.id);
    setProject(p);
    setMode("workspace");
    setTab("agent");
    setCounts({ agent: 0, characters: 0, settings: 0, scenes: 0, edit: 0 });
  }

  function goGallery() {
    setMode("gallery");
  }

  function enterWorkspaceTab(key: TabKey) {
    if (mode !== "workspace") return;
    setTab(key);
  }

  if (!authChecked) {
    return (
      <ToastProvider>
        <div className="muted" style={{ padding: 40 }}>Loading…</div>
      </ToastProvider>
    );
  }

  if (!user) {
    return (
      <ToastProvider>
        <AuthScreen onAuthed={(u) => setUser(u)} />
      </ToastProvider>
    );
  }

  return (
    <ToastProvider>
      <div className="app">
        <Sidebar
          mode={mode}
          projectName={project?.name ?? null}
          active={tab}
          onChange={enterWorkspaceTab}
          onGallery={goGallery}
          counts={counts}
        />

        <header className="topbar">
          <span className="spacer"></span>
          <span className="topbar-pill" style={{ border: "1px solid rgba(255,255,255,0.2)", color: "#fff" }}>
            {user.email}
          </span>
          <button className="topbar-pill" onClick={logout} style={{ marginLeft: 8 }}>
            Sign out
          </button>
          <KeyIndicator status={openrouter} onClick={() => setConfigOpen(true)} />
          <span className="status" style={{ marginLeft: 12 }}>
            {backendOk === null && "Connecting..."}
            {backendOk === true && <span style={{ color: "var(--success)" }}>● Backend connected</span>}
            {backendOk === false && <span style={{ color: "var(--danger)" }}>● Backend offline</span>}
          </span>
          <button
            onClick={() => setConfigOpen(true)}
            title="Settings"
            style={{ marginLeft: 12, padding: "6px 10px" }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09A1.65 1.65 0 0 0 15 4.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c.36.15.68.4.92.73.24.32.39.71.42 1.12.04.4-.04.81-.22 1.18-.18.36-.46.67-.8.87l-.12.06a1.65 1.65 0 0 0-.69 1.21V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 14 19.4z" />
            </svg>
          </button>
        </header>

        <main className="workspace">
          {backendOk === true && openrouter?.activeSource === "none" && (
            <div
              style={{
                border: "1px solid var(--warning)",
                color: "var(--warning)",
                padding: "10px 14px",
                borderRadius: "var(--radius)",
                marginBottom: 16,
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <span>⚠ No OpenRouter API key configured. Generation will fail until you add one.</span>
              <button className="primary" onClick={() => setConfigOpen(true)}>Add key</button>
            </div>
          )}
          {mode === "gallery" && <ProjectGallery onOpen={openProject} />}
          {mode === "workspace" && project && (
            <>
              {tab === "agent" && <AgentTab />}
              {tab === "characters" && <CharactersTab />}
              {tab === "settings" && <SettingsTab />}
              {tab === "scenes" && <ScenesTab />}
              {tab === "edit" && <EditTab />}
            </>
          )}
        </main>
      </div>

      {configOpen && <ConfigDialog onClose={() => setConfigOpen(false)} />}
    </ToastProvider>
  );
}

function KeyIndicator({
  status,
  onClick,
}: {
  status: OpenRouterStatus | null;
  onClick: () => void;
}) {
  if (!status) return null;
  const label =
    status.activeSource === "stored"
      ? `OpenRouter · ${status.storedKeyMasked ?? "stored"}`
      : status.activeSource === "env"
        ? "OpenRouter · env key"
        : "OpenRouter · no key";
  const color =
    status.activeSource === "none" ? "var(--danger)" : "var(--success)";
  return (
    <button
      onClick={onClick}
      title="Open OpenRouter settings"
      className="topbar-pill"
      style={{
        border: `1px solid ${color}`,
        color,
        marginLeft: 8,
      }}
    >
      {label}
    </button>
  );
}
